import React, { useContext, useEffect, useState } from "react";
import { useAppContext } from "../../context/AppContext.tsx";
import { DimensionFieldsModel, DimensionModel } from "../../_core/_model.ts";
import { AuthContext } from "../../context/AuthContext.js";
import { useToast } from "../../context/ToastContext.tsx";
import { AreObjectsEquals, GetObjectDifferences } from "../../Utilities/Utilities.ts";
import { GetDimensionFields, UpsertDimension, UpsertDimensionField } from "../../_core/_request.ts";
import { RiResetLeftLine } from "react-icons/ri";
import { FiRefreshCw, FiSave, FiX } from "react-icons/fi";
import { Spinner } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";

export function DimensionConfig() {
    const { dimensions, setDimensions } = useAppContext();
    const [dimensionLocal, setDimensionLocal] = useState<DimensionModel[]>([]);
    const [progressMessage, setProgressMessage] = useState<string | null>(null);
    const { user } = useContext(AuthContext);
    const [dimensionFields, setDimensionFields] = useState<{ [key: string]: DimensionFieldsModel[] }>({});
    const [dimensionFieldsOrig, setDimensionFieldsOrig] = useState<{ [key: string]: DimensionFieldsModel[] }>({});
    const [selectedDim, setSelectedDim] = useState<DimensionModel | null>(null);
    const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({});
    const [fieldTypeFilter, setFieldTypeFilter] = useState<number>(-1);
    const { showToast } = useToast();

    // type index -> label
    const typeOptions = ["Text", "Dropdown", "Date"]; // extend as needed

    const initDimensions = () => {
        setDimensionLocal([]);
        setTimeout(() => {
            setDimensionLocal(JSON.parse(JSON.stringify(dimensions)));
        }, 300);
    };

    const loadFields = (dimensionCode: string) => {
        setLoadingFields((p) => ({ ...p, [dimensionCode]: true }));
        GetDimensionFields(dimensionCode)
            .then((x) => {
                setDimensionFields((prev) => ({ ...prev, [dimensionCode]: x.data }));
                setDimensionFieldsOrig((prev) => ({
                    ...prev,
                    [dimensionCode]: JSON.parse(JSON.stringify(x.data || [])),
                }));
            })
            .finally(() => setLoadingFields((p) => ({ ...p, [dimensionCode]: false })));
    };

    const saveField = async (field: DimensionFieldsModel) => {
        try {
            await UpsertDimensionField(field);
            setDimensionFields((prev) => {
                const list = [...(prev[field.forDimension] || [])];
                const i = list.findIndex((f) => f.columnID === field.columnID);
                if (i > -1) list[i] = { ...field };
                return { ...prev, [field.forDimension]: list };
            });
            setDimensionFieldsOrig((prev) => {
                const list = [...(prev[field.forDimension] || [])];
                const i = list.findIndex((f) => f.columnID === field.columnID);
                if (i > -1) list[i] = JSON.parse(JSON.stringify(field));
                return { ...prev, [field.forDimension]: list };
            });
            showToast({ message: "Field saved", type: "success", duration: 3000 });
        } catch {
            showToast({ message: "Failed to save field", type: "warning", duration: 5000 });
        }
    };

    useEffect(() => {
        initDimensions();
    }, []);

    const updateStyle = (key: number, field: keyof DimensionModel, value: any) => {
        const copy = [...dimensionLocal];
        (copy[key] as any)[field] = value;
        setDimensionLocal(copy);
    };

    const cantSave = AreObjectsEquals(dimensions, dimensionLocal);

    const saveDimension = () => {
        setProgressMessage("Save in Progress");
        const modifieds: DimensionModel[] = [];
        for (const indx in dimensionLocal) {
            if (!AreObjectsEquals(dimensionLocal[indx], dimensions[indx])) {
                const tDim = { ...dimensionLocal[indx] } as DimensionModel;
                (tDim as any).byUser = user.userID;
                modifieds.push(tDim);
            }
        }
        if (modifieds.length === 0) {
            setProgressMessage(null);
            return;
        }
        let completed = 0;
        modifieds.forEach((m) => {
            const i = dimensionLocal.findIndex((d) => d.dimensionCode === m.dimensionCode);
            (m as any).dirtyFields = GetObjectDifferences(dimensionLocal[i], dimensions[i]);
            UpsertDimension(m).then((x) => {
                if (typeof x.data === "number") {
                    completed++;
                    if (completed === modifieds.length) {
                        setProgressMessage(null);
                        const nArr = JSON.parse(JSON.stringify(dimensionLocal));
                        setDimensions([...nArr]);
                    }
                    showToast({ message: "Saved successfully", type: "success", duration: 5000 });
                } else {
                    setProgressMessage(null);
                    showToast({ message: x.data, type: "warning", duration: 5000 });
                }
            });
        });
    };

    const isFieldDirty = (dimCode: string, field: DimensionFieldsModel) => {
        const orig = (dimensionFieldsOrig[dimCode] || []).find((f) => f.columnID === field.columnID);
        if (!orig) return true;
        return (orig as any).fieldType !== (field as any).fieldType;
    };

    return (
        <div className="d-flex gap-3">
            {/* LEFT: Dimensions */}
            <div className="flex-fill table-responsive" style={{ minWidth: 520 }}>
                <table className="align-middle rounded-3 mb-0 w-100">
                    <thead className="table-thead">
                        <tr>
                            <th className="text-color fw-normal j_st_d_dimCol" style={{ fontSize: 12 }}>Dimension</th>
                            <th className="text-color fw-normal j_st_d_name" style={{ fontSize: 12 }}>Name</th>
                            <th className="text-color fw-normal text-center j_st_d_clr" style={{ fontSize: 12, width: 50 }}>Text</th>
                            <th className="text-color fw-normal text-center j_st_d_bg" style={{ fontSize: 12, width: 50 }}>Background</th>
                        </tr>
                    </thead>
                    <tbody className="table-tbody">
                        {dimensionLocal.length === 0 ? (
                            [...Array(5)].map((_, i) => (
                                <tr key={i}><td colSpan={4}><div className="placeholder-glow"><span className="placeholder col-12" style={{ height: 28 }}></span></div></td></tr>
                            ))
                        ) : (
                            dimensionLocal.map((dim, indx) => (
                                <tr
                                    key={indx}
                                    className={selectedDim?.dimensionCode === dim.dimensionCode ? "selected" : ""}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                        if (selectedDim?.dimensionCode === dim.dimensionCode) {
                                            setSelectedDim(null);
                                        } else {
                                            setSelectedDim(dim);
                                            if (!dimensionFields[dim.dimensionCode]) loadFields(dim.dimensionCode);
                                        }
                                    }}
                                >
                                    <td className="fw-medium p-1">{dim.dimensionCode}</td>
                                    <td className="p-1">
                                        <input
                                            className="form-control"
                                            style={{ backgroundColor: dim.bg, color: dim.color }}
                                            defaultValue={dim.dimensionName}
                                            onChange={(e) => {
                                                const pVal = [...dimensionLocal];
                                                pVal[indx].dimensionName = e.target.value;
                                                setDimensionLocal(pVal);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </td>
                                    <td className="text-center align-middle p-1">
                                        <input
                                            type="color"
                                            className="form-control form-control-color"
                                            value={dim.color}
                                            onChange={(e) => updateStyle(indx, "color" as any, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </td>
                                    <td className="text-center align-middle p-1">
                                        <input
                                            type="color"
                                            className="form-control form-control-color"
                                            value={dim.bg}
                                            onChange={(e) => updateStyle(indx, "bg" as any, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                <div className="d-flex justify-content-end gap-2 py-2">
                    <button className={`${cantSave ? "btn btn-secondary" : "btn btn-warning"} j_st_d_reset`} disabled={cantSave} onClick={initDimensions}>
                        <RiResetLeftLine /> Reset
                    </button>
                    <button className={`${cantSave ? "btn btn-secondary" : "btn btn-primary"} j_st_d_save`} disabled={cantSave} onClick={saveDimension}>
                        {progressMessage ? (
                            <>
                                <Spinner animation="border" size="sm" /> {progressMessage}
                            </>
                        ) : (
                            "Save"
                        )}
                    </button>
                </div>
            </div>

            {/* RIGHT: Fields (only when selected) */}
            <AnimatePresence>
                {selectedDim && (
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 50 }}
                        transition={{ duration: 0.25 }}
                        className="border rounded-3 p-3"
                        style={{ width: 460, background: "var(--bs-body-bg)" }}
                    >
                        <div className="mb-2">
                            <div className="d-flex flex-row align-items-center gap-2">
                                <div className="small text-muted-theme">Fields for Dimension</div>
                                <div className="fw-semibold">{selectedDim.dimensionName}</div>
                            </div>
                            <div className="small text-muted-theme mb-2">
                                Tip: Change a field's type, then click the save icon to persist. Use the filter to narrow the list. Refresh pulls latest from server.
                            </div>
                            <div className="mb-2 d-flex gap-2">
                                <select
                                    className="form-select form-select-sm py-1"
                                    style={{ height: "35px" }}
                                    value={String(fieldTypeFilter)}
                                    onChange={(e) => setFieldTypeFilter(parseInt(e.target.value, 10))}
                                >
                                    <option value={String(-1)}>All Types</option>
                                    {typeOptions.map((t, i) => (
                                        <option key={i} value={String(i)}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    className="btn btn-light btn-sm py-0"
                                    style={{ height: "35px" }}
                                    onClick={() => loadFields(selectedDim.dimensionCode)}
                                    disabled={!!loadingFields[selectedDim.dimensionCode]}
                                >
                                    {loadingFields[selectedDim.dimensionCode] ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <>
                                            <FiRefreshCw />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {loadingFields[selectedDim.dimensionCode] ? (
                            <div className="placeholder-glow">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="placeholder col-12 mb-2" style={{ height: 28 }}></div>
                                ))}
                            </div>
                        ) : (
                            <div className="table-responsive" style={{ maxHeight: 420, overflow: "auto" }}>
                                <table className="table align-middle table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th className="small text-muted-theme">Column</th>
                                            <th className="small text-muted-theme">Name</th>
                                            <th className="small text-muted-theme" style={{ width: "100px" }}>Type</th>
                                            <th className="small text-muted-theme text-end" style={{ width: "80px" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(dimensionFields[selectedDim.dimensionCode] || [])
                                            .filter((f) => fieldTypeFilter === -1 || (f as any).fieldType === fieldTypeFilter)
                                            .map((f, rowIdx) => {
                                                const dirty = isFieldDirty(selectedDim.dimensionCode, f);
                                                const columnLabel: string = (f as any).columnDescription ?? (f as any).columnName ?? `#${(f as any).columnID}`;
                                                return (
                                                    <tr key={rowIdx} className={dirty ? "table-warning" : undefined}>
                                                        <td className="small">{(f as any).columnID}</td>
                                                        <td className="small">{columnLabel}</td>
                                                        <td>
                                                            <select
                                                                className="form-select form-select-sm w-100"
                                                                value={String((f as any).fieldType ?? -1)}
                                                                onChange={(e) => {
                                                                    const v = parseInt(e.target.value, 10);
                                                                    setDimensionFields((prev) => {
                                                                        const list = [...(prev[selectedDim.dimensionCode] || [])];
                                                                        if (rowIdx > -1) {
                                                                            const copy: any = { ...list[rowIdx] };
                                                                            copy.fieldType = v; // numeric index
                                                                            list[rowIdx] = copy;
                                                                        }
                                                                        return { ...prev, [selectedDim.dimensionCode]: list };
                                                                    });
                                                                }}
                                                            >
                                                                {typeOptions.map((label, idx) => (
                                                                    <option key={idx} value={String(idx)}>
                                                                        {label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="text-end">
                                                            {dirty ? (
                                                                <div className="btn-group btn-group-sm">
                                                                    <button className="btn btn-primary" onClick={() => saveField(f)}>
                                                                        <FiSave />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-outline-secondary"
                                                                        onClick={() => {
                                                                            const orig = (dimensionFieldsOrig[selectedDim.dimensionCode] || []).find((x) => (x as any).columnID === (f as any).columnID);
                                                                            if (!orig) return;
                                                                            setDimensionFields((prev) => {
                                                                                const list = [...(prev[selectedDim.dimensionCode] || [])];
                                                                                if (rowIdx > -1) list[rowIdx] = JSON.parse(JSON.stringify(orig));
                                                                                return { ...prev, [selectedDim.dimensionCode]: list };
                                                                            });
                                                                        }}
                                                                    >
                                                                        <FiX />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="badge bg-light text-muted-theme">Saved</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default DimensionConfig;